import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './HowItWorks.css';

const steps = [
  {
    title: 'Pin an incident on the map',
    body: 'Open the safety map and tap anywhere to drop a pin. Choose a severity level — Level 1 for discomfort or low-level concerns, Level 2 for harassment or following, Level 3 for serious or threatening situations. Add a brief description. Your report is submitted anonymously by default.',
  },
  {
    title: 'Community sees it in real time',
    body: 'Reports appear on the live safety map instantly. Color-coded markers show severity at a glance. Anyone in the area can see what has been reported nearby — without knowing who reported it.',
  },
  {
    title: 'Proximity alerts notify nearby users',
    body: 'Users who have enabled alerts receive a notification when a new report appears within their chosen area. You control the distance and severity threshold. Alerts are deduplicated so you are never spammed.',
  },
  {
    title: 'Community corroborates reports',
    body: 'Other users can upvote a report they have witnessed or experienced themselves. Upvotes are anonymous. A high upvote count signals the community has confirmed the situation — this is shown directly on the map marker.',
  },
  {
    title: 'Local moderators review and verify reports',
    body: 'Moderators are community volunteers assigned to a specific neighbourhood. They review reports for accuracy, remove spam or false posts, and can message reporters anonymously to ask for more context. Moderators do not take any physical action — their role is to keep information on the map accurate and trustworthy.',
  },
  {
    title: 'Secure anonymous inbox',
    body: 'If a moderator needs more context about your report, they can message you through the app. You receive the message in your inbox and can reply. The moderator sees only your pseudonymous user ID — never your name or email.',
  },
];

const roles = [
  {
    role: 'User',
    color: '#00897B',
    bg: '#e0f2f1',
    actions: [
      'Create a free account',
      'Submit anonymous incident reports',
      'View the community safety map',
      'Set proximity alert preferences',
      'Upvote reports you have witnessed',
      'Edit or delete your own reports',
      'Receive moderator messages in inbox',
    ],
  },
  {
    role: 'Moderator',
    color: '#F57C00',
    bg: '#fff3e0',
    actions: [
      'All user capabilities',
      'Verify reports within your assigned area',
      'Remove spam or false reports',
      'Send anonymous messages to reporters',
      'View moderator dashboard and queue',
    ],
  },
  {
    role: 'Admin',
    color: '#C62828',
    bg: '#ffebee',
    actions: [
      'All moderator capabilities',
      'Approve or deny moderator applications',
      'Assign moderator coverage areas',
      'Manage all users and accounts',
      'View area-wide incident reports',
    ],
  },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <div className="hiw-page">
      <header className="hiw-header">
        <Link to="/" className="back-button">← SafeHer</Link>
        <h1>How SafeHer Works</h1>
        <p>Anonymous reporting. Community awareness. Early warning.</p>
      </header>

      <div className="hiw-content">

        <div className="hiw-notice">
          SafeHer is a community information-sharing platform — not an emergency service.
          For any immediate danger, please contact your local emergency services directly.
          SafeHer helps communities spot patterns and stay informed early.
        </div>

        {/* Steps */}
        <section className="hiw-section">
          <h2 className="hiw-section-title">The Safety Loop</h2>
          <p className="hiw-section-sub">From a single report to community-wide awareness in minutes.</p>
          <div className="hiw-steps">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`hiw-step ${activeStep === i ? 'hiw-step--open' : ''}`}
                onClick={() => setActiveStep(activeStep === i ? null : i)}
              >
                <div className="hiw-step-header">
                  <span className="hiw-step-num">Step {i + 1}</span>
                  <span className="hiw-step-title">{step.title}</span>
                  <span className="hiw-step-chevron">{activeStep === i ? '▲' : '▼'}</span>
                </div>
                {activeStep === i && (
                  <div className="hiw-step-body">{step.body}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Privacy architecture */}
        <section className="hiw-section hiw-privacy-section">
          <h2 className="hiw-section-title">Privacy Architecture</h2>
          <p className="hiw-section-sub">Anonymity is structural — not a policy. Here's how.</p>
          <div className="hiw-privacy-grid">
            <div className="hiw-privacy-card">
              <div className="hiw-privacy-card-title">Reports</div>
              <p>Stored with a session-derived pseudonymous ID. Your name and email are never attached. Even moderators and admins cannot link a report back to your identity.</p>
            </div>
            <div className="hiw-privacy-card">
              <div className="hiw-privacy-card-title">Upvotes</div>
              <p>Each upvote can be submitted anonymously. When anonymous, your display name shows as "Anonymous" everywhere — no exceptions.</p>
            </div>
            <div className="hiw-privacy-card">
              <div className="hiw-privacy-card-title">Moderator Messages</div>
              <p>Delivered to your in-app inbox. The moderator sees only your pseudonymous user ID and the report context — never your real name or email.</p>
            </div>
            <div className="hiw-privacy-card">
              <div className="hiw-privacy-card-title">No Location History</div>
              <p>Your device location is only used in-session to center the map and calculate alert distances. We do not store location history.</p>
            </div>
          </div>
        </section>

        {/* Roles */}
        <section className="hiw-section">
          <h2 className="hiw-section-title">Who Can Do What</h2>
          <div className="hiw-roles">
            {roles.map((r) => (
              <div key={r.role} className="hiw-role-card" style={{ borderTop: `4px solid ${r.color}`, background: r.bg }}>
                <div className="hiw-role-title" style={{ color: r.color }}>{r.role}</div>
                <ul className="hiw-role-list">
                  {r.actions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Moderator vetting */}
        <section className="hiw-section">
          <h2 className="hiw-section-title">Becoming a Moderator</h2>
          <p>Anyone can apply to become a moderator from the Profile screen. Applications require:</p>
          <ul className="hiw-list">
            <li>Your full name (for admin review only — never shown publicly)</li>
            <li>A brief statement about why you want to moderate</li>
            <li>Your city or neighbourhood</li>
          </ul>
          <p>An admin reviews and approves or denies the application. Approved moderators gain access to the moderator dashboard scoped to their area only. Moderators are community volunteers — they manage information on the platform, not physical situations.</p>
        </section>

        <div className="hiw-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <span>·</span>
          <Link to="/faq">FAQ</Link>
          <span>·</span>
          <a href="mailto:support@safeher.xyz">Contact</a>
        </div>
      </div>
    </div>
  );
}
